import { INK } from './cartoon';
import type { Carve } from './terrain';

/**
 * The ground's cartoon dressing: an ink outline that hugs the solid silhouette wherever it
 * currently is (craters included) and a lighter cap band along every top surface. Both are
 * derived from the painted ground itself, so a crater punched out of the picture gets its own
 * outline and loses its grass without anyone redrawing shapes.
 */

export interface GroundArt {
  /** Flat material fills; the terrain punches craters out of this one. */
  fill: HTMLCanvasElement;
  /** The same shapes in their cap colours; the decor trims it to the top surfaces. */
  cap: HTMLCanvasElement;
  capDepth: number;
}

/** Outline thickness in world px. */
export const OUTLINE = 3.5;

const OFFSETS: [number, number][] = (() => {
  const out: [number, number][] = [];
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    out.push([Math.cos(a) * OUTLINE, Math.sin(a) * OUTLINE]);
  }
  return out;
})();

function makeCanvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = Math.max(1, w);
  c.height = Math.max(1, h);
  return c;
}

/** Sides of the picture where the ground carries on past the edge (the underground maps): there the edge column stands in for whatever hangs off it. */
export interface GroundEdges {
  left?: boolean;
  right?: boolean;
}

/**
 * drawImage with the source rectangle clamped to the image: browsers disagree about source
 * rects that hang off the edge, and we rely on "off the edge" meaning air. Except at the left and
 * right edges named in `edges`, where it means more of the same: the edge column, stretched. That is
 * two draws, so the destination must be in source-over (destination-in would erase one with the other).
 */
function blit(dst: CanvasRenderingContext2D, src: HTMLCanvasElement, sx: number, sy: number, w: number, h: number, dx: number, dy: number, edges?: GroundEdges): void {
  const x0 = Math.max(0, sx);
  const y0 = Math.max(0, sy);
  const x1 = Math.min(src.width, sx + w);
  const y1 = Math.min(src.height, sy + h);
  if (x1 <= x0 || y1 <= y0) return;
  dst.drawImage(src, x0, y0, x1 - x0, y1 - y0, dx + (x0 - sx), dy + (y0 - sy), x1 - x0, y1 - y0);
  // The stretched column reaches one px in under the real picture (same pixels there), so a fractional offset leaves no half-covered px at the join.
  if (edges?.left && sx < 0) dst.drawImage(src, 0, y0, 1, y1 - y0, dx, dy + (y0 - sy), x0 - sx + 1, y1 - y0);
  if (edges?.right && sx + w > src.width) dst.drawImage(src, src.width - 1, y0, 1, y1 - y0, dx + (x1 - sx) - 1, dy + (y0 - sy), sx + w - x1 + 1, y1 - y0);
}

export class GroundDecor {
  readonly canvas: HTMLCanvasElement;
  private scratchA: HTMLCanvasElement;
  private scratchB: HTMLCanvasElement;

  /** `edges`: the sides where the ground goes on past the picture, so the outline does not close up at the edge. */
  constructor(private readonly art: GroundArt, private readonly edges: GroundEdges = {}) {
    const w = art.fill.width;
    const h = art.fill.height;
    this.canvas = makeCanvas(w, h);
    this.scratchA = makeCanvas(w, h);
    this.scratchB = makeCanvas(w, h);
    this.refresh(0, 0, w, h);
  }

  /** A crater: the cap goes a little wider than the hole (bare dirt inside), then the edge is redrawn. */
  carve(c: Carve): void {
    const capR = c.r + this.art.capDepth * 0.8;
    const cc = this.art.cap.getContext('2d')!;
    cc.save();
    cc.globalCompositeOperation = 'destination-out';
    cc.beginPath();
    cc.arc(c.x, c.y, capR, 0, Math.PI * 2);
    cc.fill();
    cc.restore();
    this.refresh(c.x - capR, c.y - capR, c.x + capR, c.y + capR);
  }

  /** Built ground (a plank, a wall): the pictures are already painted; redraw the edge around it. */
  build(c: Carve): void {
    const w = c.w ?? 0;
    const h = c.h ?? 0;
    this.refresh(c.x - w / 2, c.y - h / 2, c.x + w / 2, c.y + h / 2);
  }

  /** Lets go of the pictures at once (setting a canvas to nothing frees its memory sooner than waiting for the collector). */
  dispose(): void {
    for (const c of [this.canvas, this.scratchA, this.scratchB]) {
      c.width = 0;
      c.height = 0;
    }
  }

  /** The mask was replaced wholesale (a late join): drop cap pixels that are air now and redraw everything. */
  resync(): void {
    const cc = this.art.cap.getContext('2d')!;
    cc.save();
    cc.globalCompositeOperation = 'destination-in';
    cc.drawImage(this.art.fill, 0, 0);
    cc.restore();
    this.refresh(0, 0, this.canvas.width, this.canvas.height);
  }

  /** Recompute the outline and cap inside a world-space box (padded for the outline's reach). */
  refresh(bx0: number, by0: number, bx1: number, by1: number): void {
    const pad = Math.ceil(OUTLINE + this.art.capDepth) + 2;
    const x0 = Math.max(0, Math.floor(bx0 - pad));
    const y0 = Math.max(0, Math.floor(by0 - pad));
    const x1 = Math.min(this.canvas.width, Math.ceil(bx1 + pad));
    const y1 = Math.min(this.canvas.height, Math.ceil(by1 + pad));
    const w = x1 - x0;
    const h = y1 - y0;
    if (w <= 0 || h <= 0) return;
    const fill = this.art.fill;
    const a = this.scratchA.getContext('2d')!;
    const b = this.scratchB.getContext('2d')!;

    // Ink ring: the silhouette minus the silhouette eroded by OUTLINE px in every direction.
    a.globalCompositeOperation = 'source-over';
    a.clearRect(0, 0, this.scratchA.width, this.scratchA.height);
    blit(a, fill, x0, y0, w, h, 0, 0);
    a.globalCompositeOperation = 'source-in';
    a.fillStyle = INK;
    a.fillRect(0, 0, w, h);

    b.globalCompositeOperation = 'source-over';
    b.clearRect(0, 0, this.scratchB.width, this.scratchB.height);
    blit(b, fill, x0, y0, w, h, 0, 0);
    const d = this.canvas.getContext('2d')!;
    b.globalCompositeOperation = 'destination-in';
    for (const [ox, oy] of OFFSETS) {
      const sx = x0 + ox;
      if ((this.edges.left && sx < 0) || (this.edges.right && sx + w > fill.width)) {
        // Past a side where the ground carries on, the edge column stands in for what hangs off the picture. That takes two
        // draws, which destination-in would let erase each other, so they are put together first, in the decor's own box
        // (nothing reads it until it is redrawn below).
        d.globalCompositeOperation = 'source-over';
        d.clearRect(x0, y0, w, h);
        blit(d, fill, sx, y0 + oy, w, h, x0, y0, this.edges);
        b.drawImage(this.canvas, x0, y0, w, h, 0, 0, w, h);
      } else blit(b, fill, sx, y0 + oy, w, h, 0, 0);
    }

    a.globalCompositeOperation = 'destination-out';
    a.drawImage(this.scratchB, 0, 0, w, h, 0, 0, w, h);

    // Cap band: cap pixels whose column has air capDepth px above them.
    b.globalCompositeOperation = 'source-over';
    b.clearRect(0, 0, this.scratchB.width, this.scratchB.height);
    blit(b, this.art.cap, x0, y0, w, h, 0, 0);
    b.globalCompositeOperation = 'destination-out';
    blit(b, fill, x0, y0 - this.art.capDepth, w, h, 0, 0);

    d.globalCompositeOperation = 'source-over';
    d.clearRect(x0, y0, w, h);
    d.drawImage(this.scratchB, 0, 0, w, h, x0, y0, w, h);
    d.drawImage(this.scratchA, 0, 0, w, h, x0, y0, w, h);
  }
}
